import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAdd,
  faKey,
  faPlus,
  faSave,
} from '@fortawesome/pro-regular-svg-icons';
import { GlobalVariables } from '../../globalVar';
import { getToast } from '../../utils/toastConfig';
import customAxios from '../../utils/customAxios';
import variables from '../../scss/_variables.scss';
import { UpdateRoleLicensesModal } from '../Modal/UpdateRoleLicensesModal';
import DeleteRoleLicensesModal from '../Modal/DeleteRoleLicensesModal';
import { useTableLocale } from '../../hooks/useTableLocale';
import { useUserPermissions } from '../../hooks/userPermissions';

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

export const LicensesTable = () => {
  const userPermissions = useUserPermissions();

  const [roleConfig, setRoleConfig] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [licensesInfo, setLicensesInfo] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [globalLicensesInfo, setGlobalLicensesInfo] = useState();
  const [globalLicensesCount, setGlobalLicensesCount] = useState(null);
  const [isEditingGlobalLicences, setIsEditingGlobalLicences] = useState(false);

  const { detailString, deleteString } = useTranslationHook();
  const locale = useTableLocale();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGlobalLicenses = async () => {
    const endpoint = `${GlobalVariables.getURL()}licenses/global/licenses`;
    const response = await customAxios.get(endpoint, {
      headers: {
        Authorization: 'bearer ' + localStorage.getItem('token'),
      },
    });

    return response.data;
  };

  const fetchRolesLicenses = async () => {
    const endpoint = `${GlobalVariables.getURL()}licenses/roles/licenses`;
    const response = await customAxios.get(endpoint, {
      headers: {
        Authorization: 'bearer ' + localStorage.getItem('token'),
      },
    });

    return response.data;
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [globalLicensesData, rolesLicensesData] = await Promise.all([
        fetchGlobalLicenses(),
        fetchRolesLicenses(),
      ]);

      const filteredRolesLicenses = rolesLicensesData.filter(
        (role) => role.role_id !== 1021
      );

      setLicensesInfo(filteredRolesLicenses);
      setGlobalLicensesInfo(globalLicensesData);
      setGlobalLicensesCount(globalLicensesData.total_licenses);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: 'role_id',
      headerName: 'ID',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'role_name',
      headerName: 'Rol',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'max_licenses',
      headerName: 'Número máximo de licencias',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'current_licenses',
      headerName: 'Licencias en uso',
      width: LOCAL_COLUMN_WIDTH,
      renderCell: (params) => {
        if (isNaN(params.row.remaining_licenses)) return params.value;

        return (
          <span
            style={{ fontWeight: 'bold', color: variables.secondaryRedDabi }}
          >
            {params.row.current_licenses}
          </span>
        );
      },
    },
    {
      field: 'remaining_licenses',
      headerName: 'Licencias restantes',
      width: LOCAL_COLUMN_WIDTH,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            params.row[col.field] ? params.row[col.field] : '-',
        }
  );

  const actionColum = [
    {
      field: 'action',
      headerName: detailString,
      headerClassName: 'equip-datagrid-actions-tour',
      width: 115,
      renderCell: (params) => {
        return (
          <div
            className='cellAction equip-datagrid-actions-tour'
            style={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {userPermissions.includes('Update Licenses') && (
              <Tooltip title={'Manejar licencias'} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setRoleConfig(params.row);
                    setShowUpdateModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faKey} />
                </IconButton>
              </Tooltip>
            )}
            {userPermissions.includes('Delete Licenses') && (
              <Tooltip title={deleteString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  disabled={params.row.id === null}
                  onClick={() => {
                    setRoleConfig(params.row);
                    setShowDeleteModal(true);
                  }}
                >
                  <DeleteIcon className='detail-icons' id='delete-icon' />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const handleEditGlobalLicences = async () => {
    if (!isEditingGlobalLicences) {
      setIsEditingGlobalLicences(true);
    } else {
      try {
        setIsLoading(true);
        const endpoint = `${GlobalVariables.getURL()}licenses/global/updateMaxLicenses/${1}`;
        const response = await customAxios.patch(
          endpoint,
          {
            max_licenses: globalLicensesCount,
          },
          {
            headers: {
              Authorization: 'bearer ' + localStorage.getItem('token'),
            },
          }
        );

        if (response.status === 200) {
          getToast({
            status: 'success',
            title: 'Licencias globales actualizadas exitosamente',
          });
          fetchData();
        }
        setIsEditingGlobalLicences(false);
      } catch (error) {
        console.log('🚀 ~ error:', error);
        getToast({
          status: 'error',
          title: error.response?.data?.message || 'Error',
          duration: 4,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Check if user has any action permissions
  const hasActionPermissions =
    userPermissions.includes('Update Licenses') ||
    userPermissions.includes('Delete Licenses');

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <div className='datatable-btns-container'>
        <div className='dt-btn-main-container'>
          <span className='dabiWebText'>Licencias</span>
        </div>
      </div>

      {globalLicensesInfo && userPermissions.includes('View Licenses') && (
        <Grid container spacing={2} mb={1}>
          <Grid item xs={4}>
            <Paper elevation={2}>
              <Box p={2} display='flex' justifyContent='space-between'>
                <Box display='flex' alignItems='center' gap={2}>
                  <Typography variant='h5'>Licencias totales:</Typography>
                  {isEditingGlobalLicences ? (
                    <TextField
                      type='number'
                      size='small'
                      margin='none'
                      sx={{ width: '25%' }}
                      value={globalLicensesCount}
                      onChange={(e) => setGlobalLicensesCount(e.target.value)}
                    />
                  ) : (
                    <Typography
                      variant='h5'
                      fontWeight='bold'
                      color={variables.secondaryRedDabi}
                    >
                      {globalLicensesInfo.total_licenses}
                    </Typography>
                  )}
                </Box>
                {userPermissions.includes('Update Licenses') && (
                  <Tooltip title='Configurar licencias globales' arrow>
                    {isEditingGlobalLicences ? (
                      <IconButton
                        style={{
                          padding: '0 15px',
                          backgroundColor: variables.secondaryRedDabi,
                          borderRadius: '5px',
                        }}
                        onClick={handleEditGlobalLicences}
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon
                          icon={faSave}
                          style={{ color: '#ffffff' }}
                        />
                      </IconButton>
                    ) : (
                      <IconButton
                        style={{
                          padding: '0 15px',
                          backgroundColor: '#eeeeee',
                          borderRadius: '5px',
                        }}
                        onClick={handleEditGlobalLicences}
                      >
                        <FontAwesomeIcon icon={faKey} />
                      </IconButton>
                    )}
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper elevation={2}>
              <Box p={2} display='flex' alignItems='center' gap={2}>
                <Typography variant='h5'>Licencias en uso:</Typography>
                <Typography
                  variant='h5'
                  fontWeight='bold'
                  color={variables.secondaryRedDabi}
                >
                  {globalLicensesInfo.current_licenses}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper elevation={2}>
              <Box p={2} display='flex' alignItems='center' gap={2}>
                <Typography variant='h5'>Licencias restantes:</Typography>
                <Typography
                  variant='h5'
                  fontWeight='bold'
                  color={variables.secondaryRedDabi}
                >
                  {globalLicensesInfo.remaining_licenses}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <UpdateRoleLicensesModal
        roleConfig={roleConfig}
        showModal={showUpdateModal}
        setShowModal={setShowUpdateModal}
        refreshData={fetchData}
      />

      <DeleteRoleLicensesModal
        roleConfig={roleConfig}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        refreshData={fetchData}
      />

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        pagination
        getRowId={(row) => row.role_id}
        loading={isLoading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'role_id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              role_id: false,
            },
          },
          pinnedColumns: hasActionPermissions ? { right: ['action'] } : {},
        }}
        rows={licensesInfo}
        columns={hasActionPermissions ? columns.concat(actionColum) : columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        slots={{
          noRowsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              Sin información de licencias
            </Stack>
          ),
          toolbar: GridToolbar,
        }}
        className='equips-datagrid-tour'
      />
    </Box>
  );
};
